import { createSelector } from 'reselect';

import type RootState from 'companion-app-components/utils/redux-store/rootState';

import {
  getIsRefreshingAll as getAllLoginsAreRefreshing,
  getIsRefreshingSome as getSomeLoginsAreRefreshing,
} from 'data/institutionLogins/selectors';

import { REDUCER_KEY } from './reducer';
import type { AppStore } from './types';

export const getAppStore = (state: RootState): AppStore => state[REDUCER_KEY];

export const getIsOffline = (state: RootState) => getAppStore(state).isOffline;

export const getServiceWorkerIsRegistered = (state: RootState) => getAppStore(state).serviceWorkerIsRegistered;
export const getServiceWorkerIsRegistering = (state: RootState) => getAppStore(state).serviceWorkerIsRegistering;
export const getServiceWorkerUpdateIsAvailable = (state: RootState) => getAppStore(state).serviceWorkerUpdateIsAvailable;

export const getAwaitingToAutoRefresh = (state: RootState) => getAppStore(state).awaitingToAutoRefresh;

export const getUpdateAllFailed = (state: RootState) => getAppStore(state).updateAllFailed;
export const getUpdateAllMessages = (state: RootState) => getAppStore(state).updateAllMessages;
export const getDBUpdateTrigger = (state: RootState) => getAppStore(state).dbUpdateTrigger;
export const getFetchInProgress = (state: RootState) => getAppStore(state).fetchInProgress;
export const getSessionStartedAt = (state: RootState) => getAppStore(state).sessionStartedAt;

export const disableRefreshButton = createSelector(
  getAwaitingToAutoRefresh,
  getSomeLoginsAreRefreshing,
  getFetchInProgress,
  (awaitingToAutoRefresh, someLoginsAreRefreshing, fetchInProgress) => awaitingToAutoRefresh || someLoginsAreRefreshing || fetchInProgress,
);

// todo: add other loading/refreshing conditions?
export const isRefreshing = createSelector(
  getAllLoginsAreRefreshing,
  getFetchInProgress,
  (allLoginsAreRefreshing, fetchInProgress) => allLoginsAreRefreshing || fetchInProgress,
);

// makeSelectLocationState expects a plain JS object for the routing state
const makeSelectLocationState = () => {
  let prevRoutingState;
  let prevRoutingStateJS;

  return (state) => {
    const routingState = state.get('route'); // or state.route

    if (!routingState.equals(prevRoutingState)) {
      prevRoutingState = routingState;
      prevRoutingStateJS = routingState.toJS();
    }

    return prevRoutingStateJS;
  };
};

export const isSanitized = (state: RootState) => getAppStore(state).isSanitized;

export const csvImportCompleteByClientId = (state: RootState) => getAppStore(state).csvImportCompleteByClientId;
// @ts-expect-error: Property 'get' does not exist on type '{}'.
export const csvImportCompleteForClientId = (state: RootState, clientId: string) => (csvImportCompleteByClientId(state) || {}).get(clientId);

export const csvImportErrorByClientId = (state: RootState) => getAppStore(state).csvImportErrorByClientId;
// @ts-expect-error: Property 'get' does not exist on type '{}'.
export const csvImportErrorForClientId = (state: RootState, clientId: string) => (csvImportErrorByClientId(state) || {}).get(clientId);

export const csvImportFilesInProgress = createSelector(
  csvImportErrorByClientId,
  csvImportCompleteByClientId,
  (state, csvImportFilesByClientId) => csvImportFilesByClientId,
  (csvImportError, csvImportComplete, csvImportFiles) =>
    csvImportFiles.filter((_csvImportFile, clientId) => !csvImportComplete.get(clientId) && !csvImportError.get(clientId)),
);

export const csvImportFilesSucceed = createSelector(
  csvImportCompleteByClientId,
  (state, csvImportFilesByClientId) => csvImportFilesByClientId,
  (csvImportComplete, csvImportFiles) =>
    csvImportComplete.filter((_csvImportFile, clientId) => csvImportFiles.get(clientId)),
);

export const csvImportFilesFailed = createSelector(
  csvImportErrorByClientId,
  csvImportCompleteByClientId,
  (state, csvImportFilesByClientId) => csvImportFilesByClientId,
  (csvImportErrors, csvImportComplete, csvImportFiles) =>
    csvImportErrors.filter((_csvImportFile, clientId) => csvImportFiles.get(clientId) && !csvImportComplete.get(clientId)),
);

export {
  makeSelectLocationState,
};
